import { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Building2, Plus, Search, Pencil, Trash2, MapPin, User, Calendar, Euro,
  X, Camera, StickyNote, Users, Info, Loader2, Trash, History,
  CheckCircle2, Image as ImageIcon, Activity, GalleryHorizontal
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import GalerieTab from "@/components/GalerieTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialFormData = {
  nom: "", adresse: "", client_name: "", date_debut: "", date_fin_prevue: "",
  statut: "en_cours", progression: "", budget: "", description: "", ouvriers_assignes: []
};

function SkeletonCard() {
  return (
    <div className="skeleton-card">
      <div className="flex items-center gap-3 mb-3">
        <div className="skeleton w-10 h-10 rounded-lg" />
        <div className="flex-1">
          <div className="skeleton h-4 w-3/4 mb-2" />
          <div className="skeleton h-3 w-1/3" />
        </div>
      </div>
      <div className="skeleton h-3 w-full mb-2" />
      <div className="skeleton h-3 w-2/3 mb-2" />
      <div className="skeleton h-2 w-full rounded-full" />
    </div>
  );
}

// =========== ACTIVITY ICON ===========
function ActivityIcon({ type }) {
  const icons = {
    note_ajoutee: <StickyNote className="w-3.5 h-3.5 text-amber-500" />,
    note_supprimee: <StickyNote className="w-3.5 h-3.5 text-gray-400" />,
    photo_ajoutee: <Camera className="w-3.5 h-3.5 text-blue-500" />,
    photo_supprimee: <ImageIcon className="w-3.5 h-3.5 text-gray-400" />,
    equipe_maj: <Users className="w-3.5 h-3.5 text-purple-500" />,
    modification: <Pencil className="w-3.5 h-3.5 text-orange-500" />,
    materiau_demande: <Building2 className="w-3.5 h-3.5 text-green-500" />,
    creation: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />,
  };
  return icons[type] || <Activity className="w-3.5 h-3.5 text-gray-400" />;
}

// =========== DETAIL DRAWER ===========
function ChantierDetail({ chantier, onClose, onUpdate }) {
  const [activeTab, setActiveTab] = useState("infos");
  const [notes, setNotes] = useState(chantier.notes || []);
  const [photos, setPhotos] = useState([]);
  const [photosCount, setPhotosCount] = useState(chantier.photos_count || 0);
  const [galerieCount, setGalerieCount] = useState(0);
  const [activites, setActivites] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [ouvriers, setOuvriers] = useState([]);
  const [allOuvriers, setAllOuvriers] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const fileInputRef = useRef(null);
  const { user, isPatron } = useAuth();
  const { t } = useLanguage();

  useEffect(() => {
    fetchPhotos();
    fetchOuvriers();
    fetchActivites();
    fetchGalerieCount();
  }, [chantier.id]);

  const fetchGalerieCount = async () => {
    try {
      const res = await axios.get(`${API}/chantiers/${chantier.id}/galerie`);
      setGalerieCount(res.data.length);
    } catch (e) {}
  };

  const fetchPhotos = async () => {
    try {
      const res = await axios.get(`${API}/chantiers/${chantier.id}/photos`);
      setPhotos(res.data);
      setPhotosCount(res.data.length);
    } catch (e) { console.error(e); }
  };

  const fetchOuvriers = async () => {
    try {
      const res = await axios.get(`${API}/users/ouvriers`);
      setAllOuvriers(res.data);
      setOuvriers(chantier.ouvriers_assignes || []);
    } catch (e) { console.error(e); }
  };

  const fetchActivites = async () => {
    try {
      const res = await axios.get(`${API}/chantiers/${chantier.id}/activites`);
      setActivites(res.data);
    } catch (e) { console.error(e); }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      const res = await axios.post(`${API}/chantiers/${chantier.id}/notes`, {
        text: newNote,
        auteur: `${user?.prenom || ''} ${user?.nom || ''}`
      });
      setNotes(prev => [...prev, res.data]);
      setNewNote("");
      fetchActivites();
      onUpdate();
      toast.success(t('noteAdded'));
    } catch (e) { toast.error(t('errorGeneric')); }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/chantiers/${chantier.id}/notes/${noteId}`);
      setNotes(prev => prev.filter(n => n.id !== noteId));
      fetchActivites();
      onUpdate();
      toast.success(t('noteDeleted'));
    } catch (e) { toast.error(t('errorGeneric')); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (photosCount >= 10) { toast.error(t('maxPhotos')); return; }
    setUploadingPhoto(true);
    try {
      const dataUrl = await compressImage(file, 800, 0.75);
      await axios.post(`${API}/chantiers/${chantier.id}/photos`, {
        data_url: dataUrl, nom: file.name, description: ""
      });
      await fetchPhotos();
      fetchActivites();
      toast.success(t('photoAdded'));
    } catch (e) {
      if (e.response?.data?.detail) toast.error(e.response.data.detail);
      else toast.error(t('errorGeneric'));
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const deletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API}/chantiers/${chantier.id}/photos/${photoId}`);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      setPhotosCount(prev => prev - 1);
      fetchActivites();
      toast.success(t('photoDeleted'));
    } catch (e) { toast.error(t('errorGeneric')); }
  };

  const compressImage = (file, maxWidth, quality) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width, height = img.height;
          if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
          canvas.width = width; canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const toggleOuvrier = (ouvrId) => {
    setOuvriers(prev => prev.includes(ouvrId) ? prev.filter(id => id !== ouvrId) : [...prev, ouvrId]);
  };

  const saveOuvriers = async () => {
    try {
      await axios.put(`${API}/chantiers/${chantier.id}/ouvriers`, ouvriers);
      toast.success(t('teamUpdated'));
      fetchActivites();
      onUpdate();
    } catch (e) { toast.error(t('errorGeneric')); }
  };

  const getStatusBadge = (statut) => {
    const styles = {
      en_cours: "bg-blue-50 text-blue-600 border-blue-200",
      termine: "bg-emerald-50 text-emerald-600 border-emerald-200",
      en_pause: "bg-amber-50 text-amber-600 border-amber-200"
    };
    const labels = { en_cours: t('inProgress'), termine: t('completed'), en_pause: t('paused') };
    return <span className={`px-2 py-0.5 rounded-md text-xs font-medium border ${styles[statut] || styles.en_cours}`}>{labels[statut] || statut}</span>;
  };

  const getActivityLabel = (type) => ({
    note_ajoutee: t('actNote'),
    note_supprimee: t('actNoteDeleted'),
    photo_ajoutee: t('actPhoto'),
    photo_supprimee: t('actPhotoDeleted'),
    equipe_maj: t('actTeam'),
    modification: t('actUpdate'),
    materiau_demande: t('actMaterial'),
    creation: t('actCreated'),
  }[type] || type);

  const tabs = [
    { id: "infos", icon: Info, label: t('tabInfos') },
    { id: "notes", icon: StickyNote, label: t('tabNotes'), badge: notes.length },
    { id: "galerie", icon: GalleryHorizontal, label: t('tabGalerie'), badge: galerieCount > 0 ? galerieCount : null },
    { id: "equipe", icon: Users, label: t('tabEquipe'), badge: ouvriers.length },
    { id: "history", icon: History, label: t('tabHistory'), badge: activites.length > 0 ? activites.length : null }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] flex flex-col animate-fadeIn">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#F1F5F9]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#3B82F6]" />
            </div>
            <div>
              <h2 className="font-bold text-[#1E293B] font-['Barlow_Condensed'] text-xl uppercase">{chantier.nom}</h2>
              <div className="flex items-center gap-2">{getStatusBadge(chantier.statut)}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-[#94A3B8] hover:text-[#1E293B] hover:bg-[#F1F5F9] rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#F1F5F9] overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-shrink-0 flex flex-col items-center gap-0.5 py-2.5 px-2 text-xs font-medium transition-all relative ${activeTab === tab.id ? 'text-[#3B82F6] border-b-2 border-[#3B82F6] -mb-px' : 'text-[#94A3B8] hover:text-[#64748B]'}`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="whitespace-nowrap">{tab.label}</span>
              {tab.badge > 0 && (
                <span className="absolute top-1 right-0.5 min-w-[16px] h-4 rounded-full bg-[#F97316] text-white text-[9px] flex items-center justify-center font-bold px-1">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">

          {/* INFOS TAB */}
          {activeTab === "infos" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                  <p className="text-xs text-[#94A3B8] mb-1">{t('chantierClient')}</p>
                  <p className="font-semibold text-[#1E293B] text-sm">{chantier.client_name}</p>
                </div>
                {isPatron() && (
                  <div className="p-3 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                    <p className="text-xs text-[#94A3B8] mb-1">{t('chantierBudget')}</p>
                    <p className="font-semibold text-[#1E293B] text-sm font-mono">{chantier.budget?.toLocaleString('fr-FR')} €</p>
                  </div>
                )}
              </div>
              <div className="p-3 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                <p className="text-xs text-[#94A3B8] mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {t('chantierAddress')}</p>
                <p className="font-medium text-[#1E293B] text-sm">{chantier.adresse}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                  <p className="text-xs text-[#94A3B8] mb-1">{t('chantierStart')}</p>
                  <p className="font-medium text-[#1E293B] text-sm">{chantier.date_debut}</p>
                </div>
                <div className="p-3 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                  <p className="text-xs text-[#94A3B8] mb-1">{t('chantierEnd')}</p>
                  <p className="font-medium text-[#1E293B] text-sm">{chantier.date_fin_prevue}</p>
                </div>
              </div>
              <div className="p-3 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-xs text-[#94A3B8]">{t('chantierProgress')}</p>
                  <span className="font-mono font-bold text-[#1E293B]">{chantier.progression}%</span>
                </div>
                <div className="h-2.5 bg-[#E2E8F0] rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#3B82F6] to-[#2563EB] transition-all" style={{ width: `${chantier.progression}%` }} />
                </div>
              </div>
              {chantier.description && (
                <div className="p-3 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                  <p className="text-xs text-[#94A3B8] mb-1">{t('description')}</p>
                  <p className="text-[#475569] text-sm">{chantier.description}</p>
                </div>
              )}
            </div>
          )}

          {/* NOTES TAB */}
          {activeTab === "notes" && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder={t('notePlaceholder')}
                  className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-xl min-h-[80px] text-sm resize-none"
                  onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) { e.preventDefault(); addNote(); } }}
                />
              </div>
              <Button onClick={addNote} disabled={!newNote.trim()} className="w-full bg-[#3B82F6] hover:bg-blue-600 text-white rounded-xl btn-scale">
                <Plus className="w-4 h-4 mr-2" /> {t('addNoteBtn')}
              </Button>
              {notes.length === 0 ? (
                <div className="text-center py-8 text-[#94A3B8]">
                  <StickyNote className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p className="font-medium">{t('noNotes')}</p>
                  <p className="text-sm mt-1">{t('noNotesHint')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {[...notes].reverse().map(note => (
                    <div key={note.id} className="p-3 bg-amber-50 border border-amber-100 rounded-xl group hover:border-amber-200 transition-colors">
                      <div className="flex justify-between items-start">
                        <p className="text-[#1E293B] text-sm flex-1 pr-2">{note.text}</p>
                        <button onClick={() => deleteNote(note.id)} className="p-1 text-[#94A3B8] hover:text-red-500 rounded opacity-0 group-hover:opacity-100 transition-all">
                          <Trash className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <p className="text-xs text-[#94A3B8] mt-1">
                        {note.auteur && `${note.auteur} · `}
                        {new Date(note.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* GALERIE TAB */}
          {activeTab === "galerie" && (
            <GalerieTab chantier={chantier} onUpdate={() => { fetchGalerieCount(); onUpdate(); }} />
          )}

          {/* EQUIPE TAB */}
          {activeTab === "equipe" && (
            <div className="space-y-3">
              <p className="text-sm text-[#94A3B8]">{t('selectWorkers')}</p>
              {allOuvriers.length === 0 ? (
                <div className="text-center py-8 text-[#94A3B8]">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p className="font-medium">{t('noOuvriers')}</p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    {allOuvriers.map(ouv => (
                      <div
                        key={ouv.id}
                        onClick={() => isPatron() && toggleOuvrier(ouv.id)}
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${isPatron() ? 'cursor-pointer' : ''} ${ouvriers.includes(ouv.id) ? 'border-[#3B82F6] bg-blue-50' : 'border-[#E2E8F0] bg-white hover:bg-[#F8FAFC]'}`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center font-bold text-sm ${ouvriers.includes(ouv.id) ? 'bg-[#3B82F6] text-white' : 'bg-[#F1F5F9] text-[#94A3B8]'}`}>
                          {ouv.prenom?.[0]}{ouv.nom?.[0]}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-[#1E293B] text-sm">{ouv.prenom} {ouv.nom}</p>
                          <p className="text-xs text-[#94A3B8]">{ouv.email}</p>
                        </div>
                        {ouvriers.includes(ouv.id) && (
                          <div className="w-5 h-5 rounded-full bg-[#3B82F6] flex items-center justify-center">
                            <span className="text-white text-xs">✓</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {isPatron() && (
                    <Button onClick={saveOuvriers} className="w-full bg-[#3B82F6] hover:bg-blue-600 text-white rounded-xl btn-scale">
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      {t('saveTeam').replace('{count}', ouvriers.length).replace('{s}', ouvriers.length !== 1 ? 's' : '')}
                    </Button>
                  )}
                </>
              )}
            </div>
          )}

          {/* HISTORY TAB */}
          {activeTab === "history" && (
            <div className="space-y-2">
              {activites.length === 0 ? (
                <div className="text-center py-8 text-[#94A3B8]">
                  <History className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p className="font-medium">{t('noHistory')}</p>
                  <p className="text-sm mt-1">{t('historyHint')}</p>
                </div>
              ) : (
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-[#E2E8F0]" />
                  <div className="space-y-3">
                    {activites.map((act, i) => (
                      <div key={act.id} className="flex gap-3 items-start pl-2">
                        <div className="relative z-10 w-8 h-8 rounded-full bg-white border-2 border-[#E2E8F0] flex items-center justify-center flex-shrink-0">
                          <ActivityIcon type={act.type} />
                        </div>
                        <div className="flex-1 pb-3 min-w-0">
                          <p className="text-sm font-medium text-[#1E293B]">{getActivityLabel(act.type)}</p>
                          {act.description && (
                            <p className="text-xs text-[#64748B] mt-0.5 truncate">{act.description}</p>
                          )}
                          <p className="text-xs text-[#94A3B8] mt-1">
                            {act.auteur && `${act.auteur} · `}
                            {new Date(act.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Photo fullscreen viewer */}
      {selectedPhoto && (
        <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center" onClick={() => setSelectedPhoto(null)}>
          <button className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors">
            <X className="w-6 h-6" />
          </button>
          <img
            src={selectedPhoto.data_url}
            alt={selectedPhoto.nom}
            className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            onClick={e => e.stopPropagation()}
          />
          <div className="absolute bottom-4 text-white/70 text-sm bg-black/40 px-4 py-2 rounded-full">
            {new Date(selectedPhoto.created_at).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
      )}
    </div>
  );
}

// =========== MAIN CHANTIERS PAGE ===========
export default function Chantiers() {
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editingChantier, setEditingChantier] = useState(null);
  const [detailChantier, setDetailChantier] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const { user, isPatron } = useAuth();
  const { t } = useLanguage();

  useEffect(() => { fetchChantiers(); }, []);

  const fetchChantiers = async () => {
    try {
      const params = new URLSearchParams();
      if (user?.id) params.append('user_id', user.id);
      if (user?.role) params.append('role', user.role);
      const response = await axios.get(`${API}/chantiers?${params.toString()}`);
      setChantiers(response.data);
    } catch (error) { toast.error(t('errorLoading')); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        progression: parseInt(formData.progression) || 0,
        budget: parseFloat(formData.budget) || 0,
      };
      if (editingChantier) {
        await axios.put(`${API}/chantiers/${editingChantier.id}`, submitData);
        toast.success(t('siteUpdated'));
      } else {
        await axios.post(`${API}/chantiers`, submitData);
        toast.success(t('siteCreated'));
      }
      setFormOpen(false);
      setFormData(initialFormData);
      setEditingChantier(null);
      fetchChantiers();
    } catch (error) { toast.error(t('errorSaving')); }
  };

  const handleEdit = (chantier, e) => {
    e.stopPropagation();
    setEditingChantier(chantier);
    setFormData({ ...chantier, progression: String(chantier.progression ?? ""), budget: String(chantier.budget ?? "") });
    setFormOpen(true);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (window.confirm(t('confirmDeleteChantier'))) {
      try {
        await axios.delete(`${API}/chantiers/${id}`);
        toast.success(t('siteDeleted'));
        fetchChantiers();
        if (detailChantier?.id === id) setDetailChantier(null);
      } catch (error) { toast.error(t('errorGeneric')); }
    }
  };

  const filtered = chantiers.filter(c => {
    const matchSearch = c.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.adresse.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.client_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = filterStatus === "all" || c.statut === filterStatus;
    return matchSearch && matchStatus;
  });

  const getStatusColor = (s) => ({
    en_cours: 'from-blue-500 to-blue-600',
    termine: 'from-emerald-500 to-emerald-600',
    en_pause: 'from-amber-500 to-amber-600'
  }[s] || 'from-slate-400 to-slate-500');

  const getStatusBadge = (s) => {
    const c = {
      en_cours: 'bg-blue-50 text-blue-600 border-blue-200',
      termine: 'bg-emerald-50 text-emerald-600 border-emerald-200',
      en_pause: 'bg-amber-50 text-amber-600 border-amber-200'
    };
    const l = { en_cours: t('inProgress'), termine: t('completed'), en_pause: t('paused') };
    return <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium border ${c[s] || c.en_cours}`}>{l[s] || s}</span>;
  };

  if (loading) {
    return (
      <div className="space-y-5 animate-fadeIn">
        <div className="flex items-center justify-between">
          <div><div className="skeleton h-8 w-40 mb-2" /><div className="skeleton h-4 w-28" /></div>
          <div className="skeleton h-10 w-40 rounded-lg" />
        </div>
        <div className="skeleton h-10 w-64 rounded-lg" />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map(i => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="chantiers-page" className="space-y-5 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#1E293B] uppercase tracking-tight font-['Barlow_Condensed']">{t('chantiersTitle')}</h1>
          <p className="text-[#94A3B8] text-sm mt-0.5">
            {chantiers.length} {chantiers.length === 1 ? t('chantierCount').replace('{0}', '') : t('chantiersCount').replace('{0}', '')} {user?.role === 'ouvrier' ? t('assignedLabel') : t('totalLabel')}
          </p>
        </div>
        {isPatron() && (
          <Button
            data-testid="btn-new-chantier"
            onClick={() => { setEditingChantier(null); setFormData(initialFormData); setFormOpen(true); }}
            className="bg-[#3B82F6] hover:bg-blue-600 text-white rounded-lg uppercase tracking-wide text-sm font-medium btn-press shadow-md shadow-blue-200"
          >
            <Plus className="w-4 h-4 mr-2" />{t('newChantier')}
          </Button>
        )}
      </div>

      {/* Search + Filter */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <Input
            data-testid="search-chantiers"
            placeholder={t('searchChantier')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white border-[#E2E8F0] text-[#1E293B] rounded-lg shadow-sm"
          />
        </div>
        <div className="flex gap-1.5">
          {['all', 'en_cours', 'en_pause', 'termine'].map(s => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filterStatus === s ? 'bg-[#1E293B] text-white shadow-sm' : 'bg-white border border-[#E2E8F0] text-[#64748B] hover:bg-[#F8FAFC]'}`}
            >
              {s === 'all' ? 'Tous' : s === 'en_cours' ? t('inProgress') : s === 'en_pause' ? t('paused') : t('completed')}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardContent className="p-12 text-center">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-[#CBD5E1]" />
            <h3 className="text-xl font-semibold text-[#1E293B] mb-2">
              {user?.role === 'ouvrier' ? t('noAssignedChantier') : t('noChantier')}
            </h3>
            {isPatron() && (
              <Button onClick={() => { setFormData(initialFormData); setFormOpen(true); }} className="mt-3 bg-[#3B82F6] text-white rounded-lg">
                <Plus className="w-4 h-4 mr-2" />{t('createChantier')}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(chantier => (
            <Card
              key={chantier.id}
              data-testid={`chantier-card-${chantier.id}`}
              onClick={() => setDetailChantier(chantier)}
              className="bg-white border-[#E2E8F0] overflow-hidden shadow-sm cursor-pointer interactive-card group"
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${getStatusColor(chantier.statut)} flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform`}>
                      <Building2 className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <CardTitle className="text-base font-semibold text-[#1E293B] truncate group-hover:text-[#3B82F6] transition-colors">{chantier.nom}</CardTitle>
                      {getStatusBadge(chantier.statut)}
                    </div>
                  </div>
                  {isPatron() && (
                    <div className="flex gap-1 flex-shrink-0 ml-2">
                      <button
                        data-testid={`btn-edit-${chantier.id}`}
                        onClick={(e) => handleEdit(chantier, e)}
                        className="p-1.5 text-[#94A3B8] hover:text-[#3B82F6] hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`btn-delete-${chantier.id}`}
                        onClick={(e) => handleDelete(chantier.id, e)}
                        className="p-1.5 text-[#94A3B8] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2.5 pt-0">
                <div className="text-sm space-y-1.5">
                  <div className="flex items-center gap-2 text-[#64748B]">
                    <MapPin className="w-3.5 h-3.5 text-[#94A3B8] flex-shrink-0" />
                    <span className="truncate">{chantier.adresse}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#64748B]">
                    <User className="w-3.5 h-3.5 text-[#94A3B8] flex-shrink-0" />
                    <span className="truncate">{chantier.client_name}</span>
                  </div>
                  {isPatron() && (
                    <div className="flex items-center gap-2 text-[#64748B]">
                      <Euro className="w-3.5 h-3.5 text-[#94A3B8] flex-shrink-0" />
                      <span className="font-mono font-semibold text-[#1E293B]">{chantier.budget?.toLocaleString('fr-FR')} €</span>
                    </div>
                  )}
                </div>

                {/* Progress bar */}
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-[#94A3B8]">{t('progression')}</span>
                    <span className="font-mono font-bold text-[#1E293B]">{chantier.progression}%</span>
                  </div>
                  <div className="h-2 bg-[#EEF2F7] rounded-full overflow-hidden">
                    <div className={`h-full rounded-full bg-gradient-to-r ${getStatusColor(chantier.statut)} transition-all`} style={{ width: `${chantier.progression}%` }} />
                  </div>
                </div>

                {/* Mini indicators */}
                <div className="flex items-center gap-3 pt-1 border-t border-[#F1F5F9] text-xs text-[#94A3B8]">
                  {(chantier.photos_count || 0) > 0 && (
                    <span className="flex items-center gap-1 bg-blue-50 text-blue-500 px-2 py-0.5 rounded-full font-medium">
                      <Camera className="w-3 h-3" />{chantier.photos_count}
                    </span>
                  )}
                  {chantier.notes?.length > 0 && (
                    <span className="flex items-center gap-1 bg-amber-50 text-amber-500 px-2 py-0.5 rounded-full font-medium">
                      <StickyNote className="w-3 h-3" />{chantier.notes.length}
                    </span>
                  )}
                  {chantier.ouvriers_assignes?.length > 0 && (
                    <span className="flex items-center gap-1 bg-purple-50 text-purple-500 px-2 py-0.5 rounded-full font-medium">
                      <Users className="w-3 h-3" />{chantier.ouvriers_assignes.length}
                    </span>
                  )}
                  <span className="ml-auto text-[#3B82F6] font-medium text-xs">{t('viewDetails')} →</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Drawer */}
      {detailChantier && (
        <ChantierDetail
          chantier={detailChantier}
          onClose={() => setDetailChantier(null)}
          onUpdate={() => {
            fetchChantiers();
            const updated = chantiers.find(c => c.id === detailChantier.id);
            if (updated) setDetailChantier(updated);
          }}
        />
      )}

      {/* Form Dialog */}
      {isPatron() && (
        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent className="bg-white border-[#E2E8F0] text-[#1E293B] max-w-lg max-h-[90vh] overflow-y-auto shadow-xl" aria-describedby="chantier-form-desc">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold uppercase font-['Barlow_Condensed'] text-[#1E293B]">
                {editingChantier ? t('editChantier') : t('newChantier')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-[#475569] font-medium text-sm">{t('chantierName')} *</Label>
                <Input data-testid="input-nom" required value={formData.nom} onChange={(e) => setFormData({...formData, nom: e.target.value})} className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#475569] font-medium text-sm">{t('address')} *</Label>
                <Input data-testid="input-adresse" required value={formData.adresse} onChange={(e) => setFormData({...formData, adresse: e.target.value})} className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#475569] font-medium text-sm">{t('client')} *</Label>
                <Input data-testid="input-client" required value={formData.client_name} onChange={(e) => setFormData({...formData, client_name: e.target.value})} className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[#475569] font-medium text-sm">{t('startDate')} *</Label>
                  <Input type="date" required value={formData.date_debut} onChange={(e) => setFormData({...formData, date_debut: e.target.value})} className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#475569] font-medium text-sm">{t('expectedEndDate')} *</Label>
                  <Input type="date" required value={formData.date_fin_prevue} onChange={(e) => setFormData({...formData, date_fin_prevue: e.target.value})} className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[#475569] font-medium text-sm">{t('status')}</Label>
                  <Select value={formData.statut} onValueChange={(v) => setFormData({...formData, statut: v})}>
                    <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-[#E2E8F0] text-[#1E293B] shadow-lg">
                      <SelectItem value="en_cours">{t('inProgress')}</SelectItem>
                      <SelectItem value="en_pause">{t('paused')}</SelectItem>
                      <SelectItem value="termine">{t('completed')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#475569] font-medium text-sm">{t('progression')} (%)</Label>
                  <Input data-testid="input-progression" type="number" min="0" max="100" value={formData.progression} placeholder="0" onChange={(e) => setFormData({...formData, progression: e.target.value})} className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#475569] font-medium text-sm">{t('budget')} (€)</Label>
                <Input data-testid="input-budget" type="number" min="0" step="100" value={formData.budget} placeholder="0" onChange={(e) => setFormData({...formData, budget: e.target.value})} className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#475569] font-medium text-sm">{t('description')}</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg min-h-[70px]" />
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setFormOpen(false)} className="border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9] rounded-lg">
                  {t('cancel')}
                </Button>
                <Button data-testid="btn-submit-chantier" type="submit" className="bg-[#3B82F6] hover:bg-blue-600 text-white rounded-lg shadow-md shadow-blue-200">
                  {editingChantier ? t('update') : t('create')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
